import { BillingStatus, SubscriptionInfo, SubscriptionTier } from '@/graphql/graphql';
import { atom } from 'jotai';

// Default subscription state for free tier
const defaultSubscription: SubscriptionInfo = {
  tier: 'FREE' as SubscriptionTier,
  billingStatus: 'NONE' as BillingStatus,
  collectionMethod: null,
  billingInterval: null,
  storageBlocks: 1,
  storageLimitBytes: 100000000000, // 100GB default
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  cancelAt: null,
  scheduledTier: null,
  scheduledStorageBlocks: null,
  scheduledChangeAt: null,
  trialEndsAt: null,
};

// Main subscription atom
export const subscriptionAtom = atom<SubscriptionInfo>(defaultSubscription);

// Derived atom for tier-based feature flags
export const subscriptionFeaturesAtom = atom((get) => {
  const subscription = get(subscriptionAtom);
  const tier = subscription.tier;

  return {
    // Tier checks
    isLite: tier === 'LITE',
    isCore: tier === 'CORE',
    isPlus: tier === 'PLUS',
    isEnterprise: tier === 'ENTERPRISE',

    // Storage limits (in bytes)
    storageLimitBytes: subscription.storageLimitBytes,
    storageBlocks: subscription.storageBlocks,

    // Project limits (null = unlimited)
    maxProjects: TIER_MAX_PROJECTS[tier],
    hasUnlimitedProjects: TIER_MAX_PROJECTS[tier] === null,

    // Billing status checks
    isTrialing: subscription.billingStatus === 'TRIALING',
    isActive: subscription.billingStatus === 'ACTIVE',
    isPastDue: subscription.billingStatus === 'PAST_DUE',
    isCanceled: subscription.billingStatus === 'CANCELED',
    isPendingPayment: subscription.billingStatus === 'PENDING_PAYMENT',
    hasNoSubscription: subscription.billingStatus === 'NONE',

    // Trial
    trialEndsAt: subscription.trialEndsAt,

    // Cancellation status
    isCancelScheduled: subscription.cancelAtPeriodEnd,
    cancelAt: subscription.cancelAt,
  };
});

// Derived atom for billing status display
export const billingStatusAtom = atom((get) => {
  const subscription = get(subscriptionAtom);

  return {
    status: subscription.billingStatus,
    currentPeriodEnd: subscription.currentPeriodEnd,
    cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
    cancelAt: subscription.cancelAt,
    collectionMethod: subscription.collectionMethod,
    billingInterval: subscription.billingInterval,
  };
});

// Helper to format storage for display
export const formatStorageBytes = (bytes: number): string => {
  const gb = bytes / (1000 * 1000 * 1000);
  if (gb >= 1000) {
    return `${(gb / 1000).toFixed(1)} TB`;
  }
  if (gb >= 1) {
    return `${gb.toFixed(0)} GB`;
  }
  const mb = bytes / (1000 * 1000);
  if (mb >= 1) {
    return `${mb.toFixed(0)} MB`;
  }
  const kb = bytes / 1000;
  return `${kb.toFixed(0)} KB`;
};

// Tier display names
export const TIER_DISPLAY_NAMES: Record<SubscriptionTier, string> = {
  FREE: 'Free',
  LITE: 'Lite',
  CORE: 'Core',
  PLUS: 'Plus',
  ENTERPRISE: 'Enterprise',
};

// Maximum projects per tier (null = unlimited)
export const TIER_MAX_PROJECTS: Record<SubscriptionTier, number | null> = {
  FREE: 5,
  LITE: 5,
  CORE: null,
  PLUS: null,
  ENTERPRISE: null,
};

// Minimum storage blocks per tier
export const TIER_MIN_STORAGE: Record<SubscriptionTier, number> = {
  FREE: 0,
  LITE: 1,
  CORE: 2,
  PLUS: 2,
  ENTERPRISE: 5,
};

// Billing status display names
export const BILLING_STATUS_DISPLAY: Record<BillingStatus, { label: string; color: string }> = {
  NONE: { label: 'No Subscription', color: 'gray' },
  TRIALING: { label: 'Trial', color: 'blue' },
  ACTIVE: { label: 'Active', color: 'green' },
  PAST_DUE: { label: 'Past Due', color: 'red' },
  UNPAID: { label: 'Unpaid', color: 'red' },
  CANCELED: { label: 'Canceled', color: 'gray' },
  PENDING_PAYMENT: { label: 'Pending Payment', color: 'yellow' },
};
